import { sq } from '@gardener/core';
import { TICK_RATE, ticks } from './config';
import type { MapDef } from './map';
import { VISION } from './vision';

/**
 * A battle royale's ways through the air (ADR-0198), the map maker's (`MapDef.mushrooms`, `washlines`, `ladders`,
 * `sunflowers`): mushrooms that bounce you over whatever is in your way, washing lines you ride from post to post, a
 * tower's ladder to climb and glide off on a leaf, pumpkin mortars that throw you across the island (ADR-0198 amended:
 * a sunflower's spot, drawn so since; the data keeps the name). Up in the air (`AIRBORNE`) nothing hits you and you hold
 * your fire; on a line or a ladder, or sat in a pumpkin mortar, you can be shot. And one way under the ground (ADR-0211, `MapDef.rails`): mine carts down the underground's rails.
 */
export const AIR = {
  /**
   * Walk into a mushroom (within `reach` px of its cap's middle) and it bounces you `far` px the way you walk (your keys;
   * the nearest clear ground to that, over walls, water and houses, as far as `over` px: a river crossed on a slant is
   * wider than `far`), `ticks` in the air, `height` px up at the top.
   */
  mushroom: { reach: 10, far: 112, over: 160, ticks: ticks(0.66), height: 40 },
  /**
   * SPACE within `reach` px of a washing line's end: you ride it to its other end at `speed` px/s, hanging `height` px off
   * the ground (shots reach you, and yours fly); SPACE again lets go, `drop` ticks down, and at its end you hop off it,
   * `rise` px up first, `hop` ticks down (ADR-0198 amended). Let go, you keep its speed the way you rode till you are down
   * (`lineLanding`). Its posts stand `post` px past its ends.
   */
  line: { reach: 14, speed: 230, height: 12, drop: ticks(0.22), hop: ticks(0.3), rise: 8, post: 9 },
  /**
   * SPACE at a ladder's foot: you climb it in `climb` ticks, then glide off its top under a leaf at `speed` px/s, sinking
   * `sink` px/s from the ladder's height — steered (ADR-0198 amended): a key held turns your way `steer` of the way to it
   * each tick and drives you on at up to `fast` px/s, the keys let go ease you back to `speed` the way you go; come down
   * over water or a roof, you skim on for at most `skim` ticks to open ground.
   */
  glide: { reach: 13, climb: ticks(0.95), speed: 118, fast: 165, steer: 0.15, sink: 40, skim: ticks(1.5) },
  /**
   * SPACE within `reach` px of a pumpkin mortar's middle (ADR-0198 amended; the map's `sunflowers`): you climb in over
   * `wind` ticks, then sit in it while you aim (SPACE again: out, onto the nearest clear ground). A click throws you once
   * you are in (one made sooner waits): the way you aim give or take `scatter` rad, `range` px give or take `spread` of
   * that (ADR-0198 amended: you pick the way, not how far), onto the nearest clear ground to it. Its last stretch is a glide
   * under a leaf (a parachute, the watchtower's glide: steered the same way), opened `chute` px up and as far short of the
   * spot as such a glide goes, at most `chuteShare` of the way (lower on a shorter throw, one stopped by the map's edge):
   * left alone, it comes down on the spot (`flingChute`). The flight up to it takes `time` s and a second more every `pace`
   * px, and peaks `peak` px up and `rise` px more every px it goes.
   */
  sunflower: {
    reach: 22,
    wind: ticks(0.7),
    scatter: 0.08,
    spread: 0.06,
    range: 520,
    time: 0.95,
    pace: 620,
    peak: 64,
    rise: 0.2,
    chute: 72,
    chuteShare: 0.55,
  },
  /**
   * A battle royale's launch off its lobby island (ADR-0200, amended 2026-11-04), as the round starts: every gardener on
   * the island waits there (`FlightKind.Wait`) for its squad's moment, drawn on the secret within `spread` ticks (every
   * squad's alike, players' and bots', so no two squads go at once and nobody's wait says who is a bot), then its whole
   * squad goes together to its spawn (a shuffle of the map's on the round's seed: nobody picks where), within `scatter`
   * px (`launchTarget`), mates a step aside each. Going, an Ejector Seat pops up under it and its rocket lights for
   * `sprout` ticks, then it is carried off over the water: the flight takes `time` s and a second more every `pace` px,
   * peaking `peak` px up and `rise` px more every px. Past its top it dives (ADR-0200 amended 2026-11-04, later): a key
   * held drifts it aside at up to `dive` px/s (eased as a glide's steering), its whole way with it. Its last stretch is a
   * glide (the watchtower's) from `glideAt` px up, begun as far short of the spot as such a glide goes (`launchLead`): the
   * umbrella opens high and early on the way down (ADR-0200 amended), a long glide everyone steers. Up there you see
   * `sight` px further round for every px up (`launchSight`).
   */
  launch: {
    spread: ticks(2.5),
    sprout: ticks(1.1),
    time: 1,
    pace: 820,
    peak: 150,
    rise: 0.11,
    glideAt: 210,
    dive: 200,
    scatter: 14,
    sight: 0.5,
  },
  /**
   * A mine cart (ADR-0211): SPACE within `reach` px of a rail's end gets you in its cart, which rolls you to the rail's
   * other end at up to `speed` px/s, `ramp` s getting up to it and as long braking at the end (shots reach you, and yours
   * fly); SPACE again hops you out where you are. Out, you step `off` px off the rail (south of one running east and
   * west, east of one running north and south; the other side if that is no clear ground).
   */
  cart: { reach: 16, speed: 380, ramp: 0.7, off: 13 },
  /**
   * An Ejector Seat (ADR-0218, content `EJECTOR`): the right button rockets you straight up out of it, `height` px in
   * `rise` ticks (quick off the ground, slowing at the top), from open sky only (never in a house, underground or off the
   * map proper); at the top an umbrella opens and you glide down under it as off the watchtower (`glide`: steered the same
   * way, the way you aim to begin with).
   */
  eject: { rise: ticks(0.7), height: 240 },
} as const;

/** How many ticks a mine cart takes to roll `d` px (ADR-0211): at `AIR.cart.speed`, plus its ramps up and down. */
export const cartTicks = (d: number): number =>
  Math.max(1, Math.round((d / AIR.cart.speed + AIR.cart.ramp) * TICK_RATE));

/**
 * How far along its rail (0 … 1) a mine cart is `t` of its `ticks` (ADR-0211): getting up to speed over its first
 * `AIR.cart.ramp` s, braking over its last as long, steady between.
 */
export function cartAlong(t: number, ticks: number): number {
  const s = Math.max(0, Math.min(1, t / ticks));
  const k = Math.min(0.5, (AIR.cart.ramp * TICK_RATE) / ticks);
  const u = 2 * k * (1 - k);
  if (s < k) return (s * s) / u;
  if (s > 1 - k) return 1 - ((1 - s) * (1 - s)) / u;
  return (s - k / 2) / (1 - k);
}

/** How far short of where it lands a launch's glide begins (ADR-0200): as far as a glide from its height goes. */
export const launchLead = (): number => (AIR.launch.glideAt / AIR.glide.sink) * AIR.glide.speed;

/**
 * A gardener's sight `h` px up off the lobby island (ADR-0200 amended): a circle `radius` px round the point `up` px over
 * its shadow, taking in it and its shadow alike, wider the higher it flies; on the ground, its bubble.
 */
export const launchSight = (h: number): { up: number; radius: number } => ({
  up: h / 2,
  radius: VISION.radius + h * AIR.launch.sight,
});

/**
 * A pumpkin mortar's throw `d` px long (ADR-0198 amended): its leaf opens `chute` px up, `back` px short of where it lands,
 * as far as a glide from that height goes (at most `AIR.sunflower.chuteShare` of the way).
 */
export function flingChute(d: number): { back: number; chute: number } {
  const { speed, sink } = AIR.glide;
  const back = Math.max(0, Math.min((AIR.sunflower.chute / sink) * speed, d * AIR.sunflower.chuteShare));
  return { back, chute: (back / speed) * sink };
}

/** What a gardener is doing in (or on the way to) the air: none, on its feet. */
export const FlightKind = {
  None: 0,
  /** Bounced off a mushroom. */
  Bounce: 1,
  /** Riding a washing line. */
  Ride: 2,
  /** Let go of a washing line: dropping to the ground below. */
  Drop: 3,
  /** Climbing a ladder. */
  Climb: 4,
  /** Gliding off its top under a leaf. */
  Glide: 5,
  /** Climbing into a pumpkin mortar and sat in it, aiming (ADR-0198 amended). */
  Wind: 6,
  /** Thrown by it. */
  Fling: 7,
  /** Going off the lobby island (ADR-0200 amended): an Ejector Seat pops up under it and its rocket lights. */
  Sprout: 8,
  /**
   * Off the island in that seat on its rocket, over the water to where it lands (ADR-0200): past its top it dives out of
   * the seat, steered (ADR-0200 amended); a glide under its umbrella is its last stretch.
   */
  Launch: 9,
  /** In a mine cart rolling down the underground's rails (ADR-0211). */
  Cart: 10,
  /** In a car (ADR-0214: the sim's `Ride` says which and where): driving it, or riding along. */
  Car: 11,
  /** Rocketed straight up by an Ejector Seat (ADR-0218). */
  Eject: 12,
  /** Gliding down under the umbrella that opens at its top (ADR-0218): a glide, steered as off the watchtower. */
  Brolly: 13,
  /** On the lobby island as the round starts (ADR-0200 amended 2026-11-04): waiting for its squad's moment to go. */
  Wait: 14,
} as const;
export type FlightKind = (typeof FlightKind)[keyof typeof FlightKind];

/** Up in the air (or about to be, off the lobby island: ADR-0200): out of every shot's and blast's reach, holding fire. */
export const AIRBORNE: ReadonlySet<number> = new Set([
  FlightKind.Wait,
  FlightKind.Bounce,
  FlightKind.Glide,
  FlightKind.Fling,
  FlightKind.Sprout,
  FlightKind.Launch,
  FlightKind.Eject,
  FlightKind.Brolly,
]);

/** Has `map` any way through the air (ADR-0198)? */
export const hasAir = (map: MapDef): boolean =>
  map.mushrooms.length + map.washlines.length + map.ladders.length + map.sunflowers.length + map.rails.length > 0;

/** The mushroom whose cap (x, y) stands on (its index in `MapDef.mushrooms`), or -1. */
export function mushroomAt(map: MapDef, x: number, y: number): number {
  const r = AIR.mushroom.reach;
  for (let i = 0; i < map.mushrooms.length; i++) {
    const m = map.mushrooms[i] as { x: number; y: number };
    if (sq(m.x - x) + sq(m.y - y) <= r * r) return i;
  }
  return -1;
}

/**
 * Something to get on with SPACE (ADR-0198): a washing line by one of its ends (`end` 0: a, 1: b), a ladder, a
 * sunflower, a rail's mine cart by one of its ends (ADR-0211).
 */
export type Boarding =
  | { kind: 'line' | 'cart'; i: number; end: 0 | 1; x: number; y: number }
  | { kind: 'ladder'; i: number; x: number; y: number }
  | { kind: 'sunflower'; i: number; x: number; y: number };

/** The nearest thing to get on within its reach of (x, y) (where you would get on it), or null. */
export function boardingAt(map: MapDef, x: number, y: number): Boarding | null {
  let best: Boarding | null = null;
  let bd = Number.POSITIVE_INFINITY;
  const offer = (b: Boarding, reach: number) => {
    const d = sq(b.x - x) + sq(b.y - y);
    if (d <= reach * reach && d < bd) {
      bd = d;
      best = b;
    }
  };
  map.washlines.forEach((l, i) => {
    offer({ kind: 'line', i, end: 0, x: l.ax, y: l.ay }, AIR.line.reach);
    offer({ kind: 'line', i, end: 1, x: l.bx, y: l.by }, AIR.line.reach);
  });
  map.ladders.forEach((l, i) => {
    offer({ kind: 'ladder', i, x: l.x, y: l.y }, AIR.glide.reach);
  });
  map.sunflowers.forEach((s, i) => {
    offer({ kind: 'sunflower', i, x: s.x, y: s.y }, AIR.sunflower.reach);
  });
  map.rails.forEach((r, i) => {
    offer({ kind: 'cart', i, end: 0, x: r.ax, y: r.ay }, AIR.cart.reach);
    offer({ kind: 'cart', i, end: 1, x: r.bx, y: r.by }, AIR.cart.reach);
  });
  return best;
}
